import React, { useState } from "react"
import PropTypes from "prop-types"
import {
  Col,
  Row,
  Card,
  CardBody,
  Label,
  Input,
  Form,
  FormFeedback,
  Button,
  Modal,
  ModalBody,
} from "reactstrap"
import { useTranslation } from "react-i18next"
import DemographicCard from "./DemographicCard"

const Step2SamplesDemographic = ({
  validation,
  pageView,
  sampleTypeOptions,
  demographicCards,
  addDemographicCard,
  removeDemographicCard,
  toggleDemographicCard,
  updateDemographicCard,
  countryTarget,
}) => {
  const { t } = useTranslation()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [cardToDelete, setCardToDelete] = useState(null)

  const handleDeleteClick = cardId => {
    setCardToDelete(cardId)
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = () => {
    if (cardToDelete) {
      removeDemographicCard(cardToDelete)
    }
    setShowDeleteModal(false)
    setCardToDelete(null)
  }

  const handleCancelDelete = () => {
    setShowDeleteModal(false)
    setCardToDelete(null)
  }

  return (
    <>
      {/* Block 1: Select Samples */}
      <Row>
        <Col>
          <Card style={{ borderRadius: "8px", overflow: "hidden" }}>
            <div
              className="p-3"
              style={{
                backgroundColor: "#E6F8F3",
                color: "#1a5d3a",
                fontWeight: "600",
                fontSize: "1.1rem",
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
              }}
            >
              {t("demographic.selectSamples")}
            </div>
            <CardBody>
              <Form>
                <Row className="mb-3">
                  <Col md={4}>
                    <Label htmlFor="sampleType" className="form-label">
                      {t("demographic.sampleTypes")} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="sampleType"
                      name="sampleType"
                      type="select"
                      className="form-control"
                      value={validation.values.sampleType || "Car Owner Samples"}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      disabled={true}
                      invalid={
                        validation.touched.sampleType && validation.errors.sampleType
                          ? true
                          : false
                      }
                    >
                      <option value="Car Owner Samples">Car Owner Samples</option>
                    </Input>
                    {validation.touched.sampleType && validation.errors.sampleType ? (
                      <FormFeedback type="invalid">
                        {validation.errors.sampleType}
                      </FormFeedback>
                    ) : null}
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={4}>
                    <Label htmlFor="totalSamples" className="form-label">
                      {t("demographic.totalSamples")} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="totalSamples"
                      name="totalSamples"
                      type="text"
                      className="form-control"
                      placeholder={t("demographic.totalSamplesPlaceholder")}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.totalSamples}
                      invalid={
                        validation.touched.totalSamples && validation.errors.totalSamples
                          ? true
                          : false
                      }
                      disabled={true}
                    />
                    {validation.touched.totalSamples && validation.errors.totalSamples ? (
                      <FormFeedback type="invalid">
                        {validation.errors.totalSamples}
                      </FormFeedback>
                    ) : null}
                  </Col>
                  <Col md={4}>
                    <Label htmlFor="totalMaximumSamples" className="form-label">
                      {t("demographic.totalMaximumSamples")} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="totalMaximumSamples"
                      name="totalMaximumSamples"
                      type="text"
                      className="form-control"
                      placeholder={t("demographic.totalMaximumSamplesPlaceholder")}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.totalMaximumSamples}
                      invalid={
                        validation.touched.totalMaximumSamples && validation.errors.totalMaximumSamples
                          ? true
                          : false
                      }
                      disabled={true}
                    />
                    {validation.touched.totalMaximumSamples && validation.errors.totalMaximumSamples ? (
                      <FormFeedback type="invalid">
                        {validation.errors.totalMaximumSamples}
                      </FormFeedback>
                    ) : null}
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Block 2: Select Demographic */}
      <Row>
        <Col>
          <Card style={{ borderRadius: "8px", overflow: "hidden" }}>
            <div
              className="p-3"
              style={{
                backgroundColor: "#E6F8F3",
                color: "#1a5d3a",
                fontWeight: "600",
                fontSize: "1.1rem",
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
              }}
            >
              {t("demographic.selectDemographic")} <span className="text-danger">*</span>
            </div>
            <CardBody>
              {demographicCards.length === 0 && (
                <p className="text-muted text-center">
                  {t("demographic.noDemographicCards")}
                </p>
              )}

              {demographicCards.map((card, index) => (
                <DemographicCard
                  key={card.id}
                  card={card}
                  index={index}
                  pageView={pageView}
                  onToggle={toggleDemographicCard}
                  onUpdate={updateDemographicCard}
                  onDelete={handleDeleteClick}
                  countryTarget={countryTarget}
                />
              ))}

              {/* Add Button - Centered below cards */}
              {pageView !== "view" && (
                <div className="text-center mt-3">
                  <Button
                    type="button"
                    style={{
                      backgroundColor: "white",
                      border: "1px solid #34c38f",
                      color: "#34c38f",
                    }}
                    onClick={addDemographicCard}
                  >
                    <i className="bx bx-plus me-1"></i>
                    {t("demographic.addMore")}
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Block 3: Contact Information */}
      <Row>
        <Col>
          <Card style={{ borderRadius: "8px", overflow: "hidden" }}>
            <div
              className="p-3"
              style={{
                backgroundColor: "#E6F8F3",
                color: "#1a5d3a",
                fontWeight: "600",
                fontSize: "1.1rem",
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
              }}
            >
              {t("demographic.contactInfo")}
            </div>
            <CardBody>
              <Form>
                <Row className="mb-4">
                  <Col>
                    <h6 style={{ color: "#1a5d3a", fontWeight: "500" }}>
                      {t("demographic.contactInfoHeader")}
                    </h6>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Label htmlFor="company" className="col-md-2 col-form-label">
                    {t("demographic.company")} <span className="text-danger">*</span>
                  </Label>
                  <Col md={10}>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      className="form-control"
                      placeholder={t("demographic.companyPlaceholder")}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.company}
                      invalid={
                        validation.touched.company && validation.errors.company
                          ? true
                          : false
                      }
                      disabled={pageView === "view"}
                    />
                    {validation.touched.company && validation.errors.company ? (
                      <FormFeedback type="invalid">
                        {validation.errors.company}
                      </FormFeedback>
                    ) : null}
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Label
                    htmlFor="personInCharge"
                    className="col-md-2 col-form-label"
                  >
                    {t("demographic.personInCharge")} <span className="text-danger">*</span>
                  </Label>
                  <Col md={10}>
                    <Input
                      id="personInCharge"
                      name="personInCharge"
                      type="text"
                      className="form-control"
                      placeholder={t("demographic.personInChargePlaceholder")}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.personInCharge}
                      invalid={
                        validation.touched.personInCharge && validation.errors.personInCharge
                          ? true
                          : false
                      }
                      disabled={pageView === "view"}
                    />
                    {validation.touched.personInCharge && validation.errors.personInCharge ? (
                      <FormFeedback type="invalid">
                        {validation.errors.personInCharge}
                      </FormFeedback>
                    ) : null}
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Label htmlFor="email" className="col-md-2 col-form-label">
                    {t("demographic.email")} <span className="text-danger">*</span>
                  </Label>
                  <Col md={10}>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      className="form-control"
                      placeholder={t("demographic.emailPlaceholder")}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.email}
                      invalid={
                        validation.touched.email && validation.errors.email
                          ? true
                          : false
                      }
                      disabled={pageView === "view"}
                    />
                    {validation.touched.email && validation.errors.email ? (
                      <FormFeedback type="invalid">
                        {validation.errors.email}
                      </FormFeedback>
                    ) : null}
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Label htmlFor="telephone" className="col-md-2 col-form-label">
                    {t("demographic.telephone")} <span className="text-danger">*</span>
                  </Label>
                  <Col md={10}>
                    <Input
                      id="telephone"
                      name="telephone"
                      type="text"
                      className="form-control"
                      placeholder={t("demographic.telephonePlaceholder")}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.telephone}
                      invalid={
                        validation.touched.telephone && validation.errors.telephone
                          ? true
                          : false
                      }
                      disabled={pageView === "view"}
                    />
                    {validation.touched.telephone && validation.errors.telephone ? (
                      <FormFeedback type="invalid">
                        {validation.errors.telephone}
                      </FormFeedback>
                    ) : null}
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal size="sm" isOpen={showDeleteModal} toggle={handleCancelDelete} centered>
        <ModalBody className="px-4 py-4">
          <p
            style={{ fontSize: "16px", fontWeight: "600", color: "#000" }}
            className="mb-4"
          >
            {t("demographic.confirmDelete")}
          </p>
          <div className="d-flex gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-outline-secondary"
              style={{ minWidth: "70px" }}
              onClick={handleConfirmDelete}
            >
              {t("demographic.yes")}
            </button>
            <button
              type="button"
              className="btn btn-success"
              style={{ minWidth: "70px" }}
              onClick={handleCancelDelete}
            >
              {t("demographic.no")}
            </button>
          </div>
        </ModalBody>
      </Modal>
    </>
  )
}

Step2SamplesDemographic.propTypes = {
  validation: PropTypes.object.isRequired,
  pageView: PropTypes.string,
  sampleTypeOptions: PropTypes.array.isRequired,
  demographicCards: PropTypes.array.isRequired,
  addDemographicCard: PropTypes.func.isRequired,
  removeDemographicCard: PropTypes.func.isRequired,
  toggleDemographicCard: PropTypes.func.isRequired,
  updateDemographicCard: PropTypes.func.isRequired,
  countryTarget: PropTypes.string,
}

export default Step2SamplesDemographic
